/* qty-break-size-sync.js — Lyvoi PDP
 *
 * Fixes the "I only wanted the 22 inch but it keeps adding a 20 inch" complaint
 * (Mike Dowell, 2026-09-04; same shape in carts since 2026-08-23).
 *
 * The Duo/Triple tiers render one selector row per case. Each row is independent
 * and every row loads on the default variant, so a shopper who changes row #1 to
 * the size they want silently keeps a 20" Carry on in rows #2/#3 and only finds
 * out at checkout.
 *
 * What this does, in order of importance:
 *   1. Row #1 leads. Changing colour or size on row #1 copies to the other rows
 *      of the SAME tier, unless the shopper has deliberately set that row before
 *      (then it is left alone, because mixed sets are a real use case).
 *   2. Relabels the confusing "- Unavailable" option text: those entries are
 *      combinations that were never produced, not sold-out stock.
 *   3. Adds one line of copy to multi-case tiers saying rows follow #1 and can be
 *      changed individually.
 *
 * Display + selection only: it never touches the add-to-cart form, the submit
 * button, or any price. Every step is wrapped in try/catch — if it throws, the
 * page behaves exactly as it did before this file existed.
 */
(function () {
  'use strict';

  var ROW = '.quantity-break__selector-item';
  var TIER = '.quantity-break';
  var SELECT = 'select.quantity-break__variant-select';
  var HINT_CLASS = 'lyvoi-qb-hint';
  var HINT_TEXT =
    'Each case is chosen separately. #2 and #3 follow #1 — change them only if you want different sizes.';
  var UNAVAILABLE_RE = /\s*[-–—]\s*Unavailable\s*$/i;
  var UNAVAILABLE_TEXT = ' — not available in this color';

  var syncing = false;

  function rowIndex(row) {
    var raw = row.getAttribute('data-select-index');
    return raw === null ? -1 : parseInt(raw, 10);
  }

  function rowsOf(tier) {
    return Array.prototype.slice.call(tier.querySelectorAll(ROW));
  }

  /* Copy row #1's value into the matching select of every follower row that the
     shopper has not set themselves. */
  function mirrorFromLeadRow(leadSelect) {
    var tier = leadSelect.closest(TIER);
    var leadRow = leadSelect.closest(ROW);
    if (!tier || !leadRow || rowIndex(leadRow) !== 0) return;

    var name = leadSelect.getAttribute('name');
    var value = leadSelect.value;

    rowsOf(tier).forEach(function (row) {
      if (row === leadRow) return;
      var target = row.querySelector('select[name="' + name + '"]');
      if (!target) return;
      if (target.dataset.lyvoiUserPicked === '1') return;   // shopper chose this one
      if (target.value === value) return;

      var option = Array.prototype.filter.call(target.options, function (o) {
        return o.value === value;
      })[0];
      if (!option || option.disabled) return;               // never force a dead combo

      target.value = value;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function onChange(event) {
    var select = event.target;
    if (!select || !select.matches || !select.matches(SELECT)) return;

    try {
      var row = select.closest(ROW);
      if (!row) return;

      // A real click on a follower row means "I want this one different".
      if (event.isTrusted && rowIndex(row) > 0) {
        select.dataset.lyvoiUserPicked = '1';
        return;
      }

      if (syncing) return;
      syncing = true;
      try {
        mirrorFromLeadRow(select);
      } finally {
        syncing = false;
      }
    } catch (e) {
      syncing = false;
    }
  }

  function relabelUnavailable(root) {
    try {
      var selects = (root || document).querySelectorAll(SELECT);
      Array.prototype.forEach.call(selects, function (select) {
        Array.prototype.forEach.call(select.options, function (option) {
          if (UNAVAILABLE_RE.test(option.textContent)) {
            option.textContent = option.textContent.replace(UNAVAILABLE_RE, UNAVAILABLE_TEXT);
          }
        });
      });
    } catch (e) {}
  }

  function addHints(root) {
    try {
      var tiers = (root || document).querySelectorAll(TIER);
      Array.prototype.forEach.call(tiers, function (tier) {
        if (tier.querySelector('.' + HINT_CLASS)) return;
        if (rowsOf(tier).length < 2) return;                 // single-case tier needs no hint
        var wrap = tier.querySelector('.quantity-break__variants');
        if (!wrap) return;
        var hint = document.createElement('div');
        hint.className = HINT_CLASS;
        hint.textContent = HINT_TEXT;
        hint.style.cssText = 'font-size:.78rem;line-height:1.35;opacity:.7;margin:.35rem 0 .1rem;';
        wrap.insertBefore(hint, wrap.firstChild);
      });
    } catch (e) {}
  }

  function decorate() {
    relabelUnavailable(document);
    addHints(document);
  }

  function init() {
    document.addEventListener('change', onChange, true);
    decorate();

    // The tier markup is re-rendered when a variant changes; redecorate then.
    try {
      var container = document.querySelector('.quantity-breaks-container');
      if (container && window.MutationObserver) {
        var pending = null;
        new MutationObserver(function () {
          clearTimeout(pending);
          pending = setTimeout(decorate, 120);
        }).observe(container, { childList: true, subtree: true });
      }
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
